/**
 * Cache configuration for TTL, invalidation triggers, and storage settings
 */

import { CacheDataType } from './cacheKeys';

/**
 * Time-to-live configuration in milliseconds
 */
export const TTL_CONFIG: Record<CacheDataType, number> = {
  branches: 24 * 60 * 60 * 1000,        // 24 hours - changes when branch added/deleted
  categories: 24 * 60 * 60 * 1000,      // 24 hours - changes when category added/deleted
  menuItems: 24 * 60 * 60 * 1000,       // 24 hours - changes when menu item added/deleted/modified
  deliveryZones: 24 * 60 * 60 * 1000,   // 24 hours - changes when zone added/deleted
  orders: 2 * 60 * 1000,                // 2 minutes - optimized for performance
  tableSessionSummary: 30 * 1000        // 30 seconds - optimized for responsive updates
};

/**
 * Events that trigger cache invalidation
 */
export const INVALIDATION_TRIGGERS = {
  // User session changes
  onLogin: [] as CacheDataType[], // Don't clear cache on login - keep existing valid data
  onLogout: ['branches', 'categories', 'menuItems', 'deliveryZones', 'orders', 'tableSessionSummary'] as CacheDataType[],
  // Remove onBranchSwitch entirely - we want to keep cached data across branches
  
  // Data mutations
  onOrderCreate: ['orders', 'tableSessionSummary'] as CacheDataType[],
  onOrderStatusUpdate: ['orders', 'tableSessionSummary'] as CacheDataType[],
  onMenuItemUpdate: ['menuItems'] as CacheDataType[],
  onMenuItemCreate: ['menuItems'] as CacheDataType[],
  onMenuItemDelete: ['menuItems'] as CacheDataType[],
  onCategoryUpdate: ['categories', 'menuItems'] as CacheDataType[],
  onCategoryCreate: ['categories'] as CacheDataType[],
  onCategoryDelete: ['categories', 'menuItems'] as CacheDataType[],
  onDeliveryZoneUpdate: ['deliveryZones'] as CacheDataType[],
  onBranchUpdate: ['branches'] as CacheDataType[],
  
  // Real-time events (shorter invalidation, more targeted)
  onNewOrder: ['orders'] as CacheDataType[],
  onTableSessionChange: ['tableSessionSummary'] as CacheDataType[]
} as const;

/**
 * Cache storage configuration
 */
export const STORAGE_CONFIG = {
  // IndexedDB database name
  dbName: 'MenuMavenCache',
  
  // IndexedDB version
  dbVersion: 1,
  
  // Object store name
  storeName: 'cacheData',
  
  // Maximum cache size in MB (to prevent excessive storage usage)
  maxCacheSizeMB: 50,
  
  // Maximum number of cached items per data type
  maxItemsPerType: 1000,
  
  // Cleanup interval for expired items (in milliseconds)
  cleanupInterval: 5 * 60 * 1000, // 5 minutes
  
  // Enable compression for large objects
  enableCompression: true,
  
  // Fallback to memory storage if IndexedDB fails
  fallbackToMemory: true
};

/**
 * Cache entry metadata
 */
export interface CacheEntry<T = any> {
  key: string;
  value: T;
  timestamp: number;
  expiry: number;
  version: string;
  size: number;
  accessCount: number;
  lastAccessed: number;
}

/**
 * Cache statistics for monitoring
 */
export interface CacheStats {
  totalEntries: number;
  totalSize: number;
  hitRate: number;
  missRate: number;
  dataTypeBreakdown: Record<CacheDataType, {
    count: number;
    size: number;
    avgAge: number;
  }>;
}

/**
 * Cache performance monitoring configuration
 */
export const PERFORMANCE_CONFIG = {
  // Enable performance monitoring
  enableMonitoring: true,
  
  // Log cache operations in development
  enableLogging: import.meta.env.DEV,
  
  // Track detailed statistics
  enableDetailedStats: true,
  
  // Performance warning thresholds
  thresholds: {
    slowOperationMs: 100,
    largeCacheEntryKB: 500,
    lowHitRatePercent: 80
  }
};

/**
 * Development and debugging configuration
 */
export const DEBUG_CONFIG = {
  // Enable cache debugging in development
  enableDebug: import.meta.env.DEV,
  
  // Log all cache operations
  logOperations: false,
  
  // Validate cache integrity on operations
  enableIntegrityChecks: import.meta.env.DEV,
  
  // Export cache for debugging
  enableCacheExport: import.meta.env.DEV
};